import { useMemo, useCallback } from 'react';
import { useTellerSession } from '~/common/hooks/use-auth/use-teller-session';
import {
  buildOpenAppClickedParams,
  sendToFirebaseAnalytics,
  useFirebaseAnalytics,
} from '~/common/hooks/use-firebase-analytics';
import type { StoryOverview } from '~/common/types/story-overview';
import { routes } from '~/utils/routes';
import { createAppInstallUrl } from '~/utils/url';

export const usePaywall = (storyOverview: StoryOverview) => {
  const { session } = useTellerSession();

  const { logEvent } = useFirebaseAnalytics();
  const deeplink = `teller://s/${storyOverview.id}`;

  const eventPage = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return window.location.pathname;
  }, []);
  const eventParentPage = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return location.pathname.replace(/\/[^/]*$/, '');
  }, []);

  const becomeVipClick = useCallback(() => {
    logEvent('story_paywall_click_btn', {
      story_id: storyOverview.id,
      series_id: storyOverview.series?.id,
      is_official: storyOverview.isOfficial,
    });
    if (session?.tellerUser) {
      if (typeof window === 'undefined') return;
      window.location.href = routes.profile.planSelection.url();
    } else {
      window.location.href = routes.register.url(true);
    }
  }, [
    logEvent,
    session?.tellerUser,
    storyOverview.id,
    storyOverview.isOfficial,
    storyOverview.series?.id,
  ]);

  const handleAppLinkClick = useCallback(() => {
    sendToFirebaseAnalytics(
      'open_app_clicked',
      buildOpenAppClickedParams({
        page: eventPage,
        parentPage: eventParentPage,
        component: 'in_story_wall',
        storyId: storyOverview.id,
        seriesId: storyOverview.series?.id,
        isOfficial: storyOverview.isOfficial,
      })
    );
    if (typeof window === 'undefined') return;
    window.location.href = createAppInstallUrl({
      deepLinkValue: deeplink,
    });
  }, [
    deeplink,
    eventPage,
    eventParentPage,
    storyOverview.id,
    storyOverview.isOfficial,
    storyOverview.series?.id,
  ]);

  return {
    becomeVipClick,
    handleAppLinkClick,
  };
};
